import type { BBox } from './geometry.js';
import type { Tool } from './pens.js';

export type { Tool };

/** Um ponto ocupa 4 posições no Float32Array: x, y, pressão, t(ms). */
export const POINT_STRIDE = 4;

/**
 * Traço. Imutável depois de terminado (SPEC §5.1): apagar gera tombstone,
 * mover/redimensionar gera traços novos. É isso que torna o sync trivial.
 */
export interface Stroke {
  id: string;
  pageId: string;
  tool: Tool;
  color: string;
  size: number;
  /** 0..1 — lápis, marcador e marca-texto não são opacos */
  opacity: number;
  points: Float32Array;
  bbox: BBox;
  row: number;
  createdAt: number;
  audioOffsetMs?: number;
  deletedAt?: number;
  processedRunId?: string;
}

/** Forma serializável do traço (sync e IndexedDB): pontos em base64. */
export interface StrokeWire {
  id: string;
  pageId: string;
  tool: Tool;
  color: string;
  size: number;
  opacity: number;
  points: string;
  bbox: BBox;
  row: number;
  createdAt: number;
  audioOffsetMs?: number;
  deletedAt?: number;
  processedRunId?: string;
}

export function encodePoints(points: Float32Array): string {
  const bytes = new Uint8Array(points.buffer, points.byteOffset, points.byteLength);
  let bin = '';
  const CHUNK = 0x8000; // String.fromCharCode estoura a pilha com arrays grandes
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

export function decodePoints(b64: string): Float32Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Float32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 4);
}

export function strokeToWire(s: Stroke): StrokeWire {
  return { ...s, points: encodePoints(s.points) };
}

export function strokeFromWire(w: StrokeWire): Stroke {
  return { ...w, points: decodePoints(w.points) };
}

/** UUID v7: ordenável por tempo, o que ajuda no sync e na ordem de desenho. */
export function uuidv7(): string {
  const ts = Date.now();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[0] = (ts / 2 ** 40) & 0xff;
  bytes[1] = (ts / 2 ** 32) & 0xff;
  bytes[2] = (ts / 2 ** 24) & 0xff;
  bytes[3] = (ts / 2 ** 16) & 0xff;
  bytes[4] = (ts / 2 ** 8) & 0xff;
  bytes[5] = ts & 0xff;
  bytes[6] = 0x70 | ((bytes[6] as number) & 0x0f);
  bytes[8] = 0x80 | ((bytes[8] as number) & 0x3f);
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
